import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useBffClient } from '@redbax/shared';
import type {
  PackagePolicy,
  PackagePolicySummary,
  ApprovalRequest,
  PackageVulnerability,
  VulnerabilitySearchResult,
} from '@redbax/shared';

export function usePackagePolicies() {
  const client = useBffClient();
  return useQuery({
    queryKey: ['package-policies'],
    queryFn: () => client.get<PackagePolicySummary[]>('/bff/package-policies'),
  });
}

export function usePackagePolicyByEcosystem(ecosystem: string) {
  const client = useBffClient();
  return useQuery({
    queryKey: ['package-policy', ecosystem],
    queryFn: () =>
      client.get<PackagePolicy>(
        `/bff/package-policies/${encodeURIComponent(ecosystem)}`,
      ),
    enabled: !!ecosystem,
  });
}

export function useCreatePackagePolicy() {
  const client = useBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: {
      ecosystem: string;
      name: string;
      defaultAction: string;
      minPackageAgeDays: number;
      blockCriticalSeverity: boolean;
      blockHighSeverity: boolean;
    }) => client.post('/bff/package-policies', request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['package-policies'] });
    },
  });
}

export function useUpdatePackagePolicy(ecosystem: string) {
  const client = useBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: {
      name: string;
      isEnabled: boolean;
      defaultAction: string;
      minPackageAgeDays: number;
      blockCriticalSeverity: boolean;
      blockHighSeverity: boolean;
    }) =>
      client.put<void>(
        `/bff/package-policies/${encodeURIComponent(ecosystem)}`,
        request,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['package-policies'] });
      queryClient.invalidateQueries({ queryKey: ['package-policy', ecosystem] });
    },
  });
}

export function useAddToBlocklist(ecosystem: string) {
  const client = useBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: {
      ecosystem: string;
      packageName: string;
      versionPattern?: string | null;
      reason?: string | null;
    }) =>
      client.post(
        `/bff/package-policies/${encodeURIComponent(ecosystem)}/blocklist`,
        request,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['package-policy', ecosystem] });
    },
  });
}

export function useRemoveFromBlocklist(ecosystem: string) {
  const client = useBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      client.delete(
        `/bff/package-policies/${encodeURIComponent(ecosystem)}/blocklist/${id}`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['package-policy', ecosystem] });
    },
  });
}

export function useAddToAllowlist(ecosystem: string) {
  const client = useBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: {
      ecosystem: string;
      packageName: string;
      versionPattern?: string | null;
      expiresAt?: string | null;
    }) =>
      client.post(
        `/bff/package-policies/${encodeURIComponent(ecosystem)}/allowlist`,
        request,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['package-policy', ecosystem] });
    },
  });
}

export function useRemoveFromAllowlist(ecosystem: string) {
  const client = useBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      client.delete(
        `/bff/package-policies/${encodeURIComponent(ecosystem)}/allowlist/${id}`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['package-policy', ecosystem] });
    },
  });
}

export function useApprovalRequests(ecosystem?: string) {
  const client = useBffClient();
  const path = ecosystem
    ? `/bff/package-approval-requests?ecosystem=${encodeURIComponent(ecosystem)}`
    : '/bff/package-approval-requests';
  return useQuery({
    queryKey: ['package-approval-requests', ecosystem ?? 'all'],
    queryFn: () => client.get<ApprovalRequest[]>(path),
  });
}

export function useApproveRequest() {
  const client = useBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) =>
      client.postVoid(`/bff/package-approval-requests/${id}/approve`, { note }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['package-approval-requests'] });
      queryClient.invalidateQueries({ queryKey: ['package-policy'] });
    },
  });
}

export function useRejectRequest() {
  const client = useBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) =>
      client.postVoid(`/bff/package-approval-requests/${id}/reject`, { note }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['package-approval-requests'] });
      queryClient.invalidateQueries({ queryKey: ['package-policy'] });
    },
  });
}

export function useVulnerabilitySearch(params: {
  ecosystem?: string;
  packageName?: string;
  severity?: string;
  offset?: number;
  limit?: number;
}) {
  const client = useBffClient();
  const searchParams = new URLSearchParams();
  if (params.ecosystem) searchParams.set('ecosystem', params.ecosystem);
  if (params.packageName) searchParams.set('packageName', params.packageName);
  if (params.severity) searchParams.set('severity', params.severity);
  searchParams.set('offset', String(params.offset ?? 0));
  searchParams.set('limit', String(params.limit ?? 50));
  const query = searchParams.toString();
  const path = `/bff/package-vulnerabilities?${query}`;

  return useQuery({
    queryKey: ['package-vulnerabilities', params],
    queryFn: () => client.get<VulnerabilitySearchResult>(path),
    enabled: !!(params.ecosystem || params.packageName || params.severity),
  });
}

export function useRecentVulnerabilities(limit = 10) {
  const client = useBffClient();
  return useQuery({
    queryKey: ['package-vulnerabilities', 'recent', limit],
    queryFn: () =>
      client.get<VulnerabilitySearchResult>(
        `/bff/package-vulnerabilities?limit=${limit}`,
      ),
  });
}

export function usePackageVulnerabilities(ecosystem: string, packageName: string) {
  const client = useBffClient();
  return useQuery({
    queryKey: ['packages', ecosystem, packageName, 'vulnerabilities'],
    queryFn: () =>
      client.get<PackageVulnerability[]>(
        `/bff/packages/${encodeURIComponent(ecosystem)}/${encodeURIComponent(packageName)}/vulnerabilities`,
      ),
    enabled: !!ecosystem && !!packageName,
  });
}
